/**
 * Teacher Quiz Slice
 *
 * Redux state management for teacher quiz operations:
 * - Quiz CRUD operations
 * - Publishing and closing quizzes
 * - Viewing attempts and analytics
 * - Manual grading workflow
 */

import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // Quiz list
  quizzes: [],
  selectedQuiz: null,

  // Filters for quiz list
  filters: {
    status: 'all',       // 'all', 'draft', 'published', 'closed'
    quizType: 'all',     // 'all', 'class_mapped', 'open'
    search: '',
  },

  // Pagination
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },

  // Attempt management
  attempts: [],
  attemptsPagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  },

  // Analytics data
  analytics: null,

  // Manual grading
  pendingReviews: [],
  currentGradingAttempt: null,

  // OTP management (for open quizzes)
  currentOTP: null,
  otpExpiresAt: null,

  // Loading states
  loading: false,
  loadingAttempts: false,
  loadingAnalytics: false,
  loadingGrading: false,

  // Error state
  error: null,

  // Success messages
  successMessage: null,
};

const teacherQuizSlice = createSlice({
  name: 'teacherQuiz',
  initialState,
  reducers: {
    // ============ Loading States ============

    setLoading: (state, action) => {
      state.loading = action.payload;
    },

    setLoadingAttempts: (state, action) => {
      state.loadingAttempts = action.payload;
    },

    setLoadingAnalytics: (state, action) => {
      state.loadingAnalytics = action.payload;
    },

    setLoadingGrading: (state, action) => {
      state.loadingGrading = action.payload;
    },

    // ============ Error and Success ============

    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
      state.loadingAttempts = false;
      state.loadingAnalytics = false;
      state.loadingGrading = false;
    },

    clearError: (state) => {
      state.error = null;
    },

    setSuccessMessage: (state, action) => {
      state.successMessage = action.payload;
    },

    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },

    // ============ Quiz List Operations ============

    setQuizzes: (state, action) => {
      const { quizzes, pagination } = action.payload;
      state.quizzes = quizzes;
      state.pagination = pagination || state.pagination;
      state.loading = false;
      state.error = null;
    },

    appendQuizzes: (state, action) => {
      const { quizzes, pagination } = action.payload;
      state.quizzes = [...state.quizzes, ...quizzes];
      state.pagination = pagination;
      state.loading = false;
    },

    setSelectedQuiz: (state, action) => {
      state.selectedQuiz = action.payload;
      state.loading = false;
    },

    clearSelectedQuiz: (state) => {
      state.selectedQuiz = null;
      state.attempts = [];
      state.analytics = null;
      state.pendingReviews = [];
    },

    // ============ Quiz CRUD ============

    addQuiz: (state, action) => {
      state.quizzes.unshift(action.payload);
      state.pagination.total += 1;
      state.loading = false;
    },

    updateQuiz: (state, action) => {
      const updatedQuiz = action.payload;
      const index = state.quizzes.findIndex(q => q._id === updatedQuiz._id);
      if (index !== -1) {
        state.quizzes[index] = updatedQuiz;
      }
      if (state.selectedQuiz?._id === updatedQuiz._id) {
        state.selectedQuiz = updatedQuiz;
      }
      state.loading = false;
    },

    removeQuiz: (state, action) => {
      const quizId = action.payload;
      state.quizzes = state.quizzes.filter(q => q._id !== quizId);
      state.pagination.total -= 1;
      if (state.selectedQuiz?._id === quizId) {
        state.selectedQuiz = null;
      }
      state.loading = false;
    },

    // ============ Quiz Status ============

    updateQuizStatus: (state, action) => {
      const { quizId, status, accessCode, otp, otpExpiresAt } = action.payload;
      const quiz = state.quizzes.find(q => q._id === quizId);
      if (quiz) {
        quiz.status = status;
        if (accessCode) quiz.accessCode = accessCode;
      }
      if (state.selectedQuiz?._id === quizId) {
        state.selectedQuiz.status = status;
        if (accessCode) state.selectedQuiz.accessCode = accessCode;
      }
      // Update OTP if provided (for open quizzes)
      if (otp) {
        state.currentOTP = otp;
        state.otpExpiresAt = otpExpiresAt;
      }
      state.loading = false;
    },

    // ============ OTP Management ============

    setOTP: (state, action) => {
      const { otp, expiresAt } = action.payload;
      state.currentOTP = otp;
      state.otpExpiresAt = expiresAt;
    },

    clearOTP: (state) => {
      state.currentOTP = null;
      state.otpExpiresAt = null;
    },

    // ============ Filters ============

    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },

    resetFilters: (state) => {
      state.filters = initialState.filters;
    },

    // ============ Attempt Management ============

    setAttempts: (state, action) => {
      const { attempts, pagination } = action.payload;
      state.attempts = attempts;
      state.attemptsPagination = pagination || state.attemptsPagination;
      state.loadingAttempts = false;
    },

    clearAttempts: (state) => {
      state.attempts = [];
      state.attemptsPagination = initialState.attemptsPagination;
    },

    // ============ Analytics ============

    setAnalytics: (state, action) => {
      state.analytics = action.payload;
      state.loadingAnalytics = false;
    },

    clearAnalytics: (state) => {
      state.analytics = null;
    },

    // ============ Grading ============

    setPendingReviews: (state, action) => {
      state.pendingReviews = action.payload;
      state.loadingGrading = false;
    },

    setCurrentGradingAttempt: (state, action) => {
      state.currentGradingAttempt = action.payload;
    },

    clearGradingState: (state) => {
      state.pendingReviews = [];
      state.currentGradingAttempt = null;
    },

    updatePendingReview: (state, action) => {
      const { attemptId, questionId, gradeData } = action.payload;
      const review = state.pendingReviews.find(r => r.attemptId === attemptId);
      if (review) {
        const question = review.questions?.find(q => q.questionId === questionId);
        if (question) {
          question.marksAwarded = gradeData.marksAwarded;
          question.feedback = gradeData.feedback;
          question.reviewStatus = 'reviewed';
        }
      }
      state.loadingGrading = false;
    },

    removePendingReview: (state, action) => {
      const attemptId = action.payload;
      state.pendingReviews = state.pendingReviews.filter(r => r.attemptId !== attemptId);
    },

    // ============ Reset ============

    resetTeacherQuizState: () => initialState,
  },
});

// Export actions
export const {
  setLoading,
  setLoadingAttempts,
  setLoadingAnalytics,
  setLoadingGrading,
  setError,
  clearError,
  setSuccessMessage,
  clearSuccessMessage,
  setQuizzes,
  appendQuizzes,
  setSelectedQuiz,
  clearSelectedQuiz,
  addQuiz,
  updateQuiz,
  removeQuiz,
  updateQuizStatus,
  setOTP,
  clearOTP,
  setFilters,
  resetFilters,
  setAttempts,
  clearAttempts,
  setAnalytics,
  clearAnalytics,
  setPendingReviews,
  setCurrentGradingAttempt,
  clearGradingState,
  updatePendingReview,
  removePendingReview,
  resetTeacherQuizState,
} = teacherQuizSlice.actions;

// ============ Selectors ============

export const selectQuizzes = (state) => state.teacherQuiz.quizzes;
export const selectSelectedQuiz = (state) => state.teacherQuiz.selectedQuiz;
export const selectQuizFilters = (state) => state.teacherQuiz.filters;
export const selectQuizPagination = (state) => state.teacherQuiz.pagination;
export const selectQuizLoading = (state) => state.teacherQuiz.loading;
export const selectQuizError = (state) => state.teacherQuiz.error;
export const selectSuccessMessage = (state) => state.teacherQuiz.successMessage;

// Attempt selectors
export const selectAttempts = (state) => state.teacherQuiz.attempts;
export const selectAttemptsPagination = (state) => state.teacherQuiz.attemptsPagination;
export const selectLoadingAttempts = (state) => state.teacherQuiz.loadingAttempts;

// Analytics selectors
export const selectAnalytics = (state) => state.teacherQuiz.analytics;
export const selectLoadingAnalytics = (state) => state.teacherQuiz.loadingAnalytics;

// Grading selectors
export const selectPendingReviews = (state) => state.teacherQuiz.pendingReviews;
export const selectCurrentGradingAttempt = (state) => state.teacherQuiz.currentGradingAttempt;
export const selectLoadingGrading = (state) => state.teacherQuiz.loadingGrading;

// OTP selectors
export const selectCurrentOTP = (state) => state.teacherQuiz.currentOTP;
export const selectOTPExpiresAt = (state) => state.teacherQuiz.otpExpiresAt;

// ============ Derived Selectors ============

export const selectDraftQuizzes = (state) =>
  state.teacherQuiz.quizzes.filter(q => q.status === 'draft');

export const selectPublishedQuizzes = (state) =>
  state.teacherQuiz.quizzes.filter(q => q.status === 'published');

export const selectOpenQuizzes = (state) =>
  state.teacherQuiz.quizzes.filter(q => q.quizType === 'open');

export const selectClassMappedQuizzes = (state) =>
  state.teacherQuiz.quizzes.filter(q => q.quizType === 'class_mapped');

export const selectQuizzesNeedingGrading = (state) =>
  state.teacherQuiz.quizzes.filter(q =>
    q.statistics?.pendingReviewCount > 0
  );

export default teacherQuizSlice.reducer;
